import { Box, IconButton, Tab, Tabs } from '@mui/material';
import React, { CSSProperties, memo, useEffect, useRef } from 'react';
import { grid } from '../../../../Global/global_variables';
import usePersistentStore, { getDataLayer, getNewColorScheme, useGlobalBucket } from '../../../store/persistent/persistentStore';
import LayerTree from './LayerTree';
import Mixer from './Mixer';
import { findEnergyIndex, findTimeIndex } from '@gui/constants/Data';
import { Panels } from '@gui/constants/Panels';
import { getGrid } from '../../../../store/LoadingStore';
import { NotificationType, type DataState, type UUID } from '@visualizer/types';
import { AlignVerticalCenter01, ChevronSelectorVertical } from '@untitledui-pro/icons/line';
import { useSessionStore } from '../../../store/session/uiSessionStore';
import useNotificationStore from '../../../store/notification';
//import { ErrorButton } from '@gui/components/SentryButton';

//const isDevelopment = import.meta.env.DEV;

type DataProps = {
    isCollapsed: boolean;
    onToggleCollapse: () => void;
    panelsExpanded: Record<string, boolean>;
    style?: CSSProperties;
};

// Used to avoid depth loops (When you pass [] directly to Zustand it creates a new array and it's problematic):
const EMPTY_MIXER_ELEMENTS: any[] = [];

const Data: React.FC<DataProps> = ({ isCollapsed, onToggleCollapse, panelsExpanded, style }) => {
    const mixerMode = useGlobalBucket((s) => s.data.isMixerMode);
    const multimode = useGlobalBucket((s) => s.data.multimode);
    const mixerElements = usePersistentStore((s) => s.layers.layerStates.mixed?.mixerElements ?? EMPTY_MIXER_ELEMENTS);
    const setPanel = usePersistentStore((s) => s.setPanel);
    const setDataPanel = (updates: Partial<DataState>) => setPanel(updates, Panels.DATA);
    const setActiveLayerId = usePersistentStore((s) => s.setCurrentLayerId);
    const initialGeodataId = getGrid().getActiveLayer()?.parentSimulationSet.parentGeodata.id ?? null;
    const activeGeodataIdRef = useRef<string | null>(initialGeodataId);
    const setLoadingMessage = useSessionStore((s) => s.setLoadingMessage);

    useEffect(() => {
        const handleActiveLayerChange = (nextActiveLayerId: string | null) => {
            if (nextActiveLayerId) grid.setActiveLayerByUID(nextActiveLayerId);
            const layer = getGrid().getActiveLayer();
            if (!nextActiveLayerId || !layer) return;

            const results = layer.results ?? [];
            const tIdx = findTimeIndex(results);
            const eIdx = findEnergyIndex(results);
            const prev = getDataLayer()?.activeResultId ?? 0;
            const next = results[prev] ? prev : tIdx >= 0 ? tIdx : eIdx >= 0 ? eIdx : 0;

            usePersistentStore.getState().setDataLayer({ activeResultId: next });

            const newGeodataId = layer.parentSimulationSet.parentGeodata.id;
            const currentGeodataId = activeGeodataIdRef.current;

            if (newGeodataId !== currentGeodataId) {
                setLoadingMessage({
                    title: 'Loading geodata…',
                    description: 'Please wait while the scene updates.',
                });

                // Trick to force the GUI to see the new state of Zustand
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        if (currentGeodataId) {
                            grid.unloadGeodata(currentGeodataId as UUID);
                        }
                        activeGeodataIdRef.current = newGeodataId;

                        const storeState = usePersistentStore.getState();
                        grid.loadActiveGeodata(getNewColorScheme(), storeState.global.ambient.mode, storeState.global.context);
                        setLoadingMessage(null);
                    });
                });
            }
        };

        const store = usePersistentStore.getState();
        handleActiveLayerChange(store.layers.activeLayerID ?? null);

        const unsubscribe = usePersistentStore.subscribe(
            (state) => state.layers.activeLayerID,
            (next, prev) => {
                if (next === prev) return;
                handleActiveLayerChange(next ?? null);
            }
        );

        return unsubscribe;
    }, []);

    const tabValue = mixerMode ? 'mixer' : 'data';

    const panelClassName = `data-panel ${isCollapsed ? 'data-panel--collapsed' : ''}`;
    const expandedCount = Object.values(panelsExpanded).filter(Boolean).length;
    const disableToggle = !isCollapsed && expandedCount === 1;

    return (
        <div className={panelClassName} style={style} data-guided-tour="guided-tour-data-panel">
            <div className="data-panel-tabs">
                {/* {isDevelopment && <ErrorButton />} */}
                <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <Tabs
                            value={tabValue}
                            onChange={(_, newValue) => {
                                if (isCollapsed) return;
                                if (newValue === 'mixer' && !multimode) {
                                    const store = usePersistentStore.getState();
                                    const mixedMode = store.layers.layerStates.mixed?.VECTORIAL_SCALAR_MODE ?? 0;
                                    const mismatchedLayerNames: string[] = [];

                                    for (const el of mixerElements) {
                                        const layerMode = store.layers.layerStates[el.layerId]?.VECTORIAL_SCALAR_MODE ?? 0;
                                        if (layerMode !== mixedMode) {
                                            const layerName = getGrid().searchLayerByUID(el.layerId)?.name ?? el.layerId;
                                            mismatchedLayerNames.push(layerName);
                                        }
                                    }

                                    if (mismatchedLayerNames.length > 0) {
                                        useNotificationStore
                                            .getState()
                                            .showNotification(
                                                `Mixed mode accepts only layers with the same Value type (Vectorial or Scalar), please uniform and try again. Mismatched layers: ${mismatchedLayerNames.join(', ')}.`,
                                                NotificationType.Error
                                            );
                                        return;
                                    }
                                }
                                setDataPanel({ isMixerMode: newValue === 'mixer' });
                            }}
                            aria-label="Data panel tabs"
                            sx={{
                                '& .MuiTabs-flexContainer': {
                                    gap: 4,
                                },
                            }}
                        >
                            <Tab value="data" label="DATA" disabled={isCollapsed} data-guided-tour="guided-tour-data-tab" />
                            <Tab value="mixer" label="MIXER" disabled={isCollapsed} />
                        </Tabs>
                    </Box>

                    <IconButton
                        size="small"
                        onClick={onToggleCollapse}
                        disabled={disableToggle}
                        sx={{
                            position: 'absolute',
                            right: -8,
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                boxShadow: 'none',
                                backgroundColor: 'transparent',
                            },
                            '&:active': {
                                boxShadow: 'none',
                            },
                            '&:focus-visible': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                transition: 'transform 120ms ease',
                                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                            }}
                        >
                            {isCollapsed ? (
                                <ChevronSelectorVertical className="icon-size-small" />
                            ) : (
                                <AlignVerticalCenter01 className="icon-size-small" />
                            )}
                        </Box>
                    </IconButton>
                </Box>
            </div>

            <div className="data-panel-content">
                <div className="data-panel-scroll">
                    {tabValue === 'data' ? (
                        <Box>
                            <LayerTree onClick={setActiveLayerId} isDataTreeview={true} hideMixedLayer={true} />
                        </Box>
                    ) : (
                        <div className="data-panel-mixer">
                            <Mixer />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default memo(Data);
